"use client"
import { inputTextStyle } from "@/data/styles"
import clsx from "clsx"
import { useEffect, useState } from "react"
import Select, { StylesConfig } from "react-select"
import { ArrowDropDownIcon } from "../icons"

interface Option {
  value: string
  label: string
}

interface DropdownProps {
  options: Option[]
  value: string | string[]
  onChange: (value: string | string[]) => void
  placeholder?: string
  isMulti?: boolean
  isSearchable?: boolean
  label?: string
  showDropdownIcon?: boolean;
  dropdownIcon?: React.ReactNode; 
  className?: string
  valueContainerColor?: string
  zIndex?: number
  isDisabled?: boolean
}

export default function Dropdown({ options, value, onChange, placeholder = "Select...", isMulti = false,
  isSearchable = false, label, showDropdownIcon=false, dropdownIcon, className, valueContainerColor, 
  zIndex, isDisabled 
}: DropdownProps) {

  const [mounted, setMounted] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return <div className="h-[38px] w-[160px] bg-gray-200 rounded" />


  const selectedOption = isMulti
    ? options.filter((option) => (value as string[]).includes(option.value))
    : options.find((option) => option.value === value)

  // Handle selection
  // Only close menu automatically if NOT multi
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (option: any) => {
    if (isMulti) {
      onChange(option ? option.map((o: Option) => o.value) : [])
    } else {
      onChange(option ? option.value : "")
      setMenuOpen(false) // close single select
    }
  }

  const customStyles: StylesConfig<Option, boolean> = {
    control: (provided) => ({ ...provided, border: "none", boxShadow: "none", padding: 0, minHeight: "auto", backgroundColor: "transparent", zIndex: zIndex || 40, cursor: 'pointer' }),
    valueContainer: (provided) => ({ ...provided, padding: 0, zIndex: zIndex || 40, display: 'flex', alignItems: 'center', flex: 1 }),
    singleValue: (provided) => ({ ...provided, display: 'flex', flex: 1, color: valueContainerColor ?? "#32363a" }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0, zIndex: zIndex || 40 }),
    option: (provided, state) => ({ ...provided, backgroundColor: state.isFocused ? "#F3F4F6" : "#fff", color: "#111827", cursor: "pointer", zIndex: zIndex || 40 }),
    menu: (provided) => ({ ...provided, backgroundColor: 'white', boxShadow: "0 4px 10px rgba(0,0,0,0.1)", zIndex: zIndex || 40 }),
    menuPortal: (provided) => ({ ...provided, zIndex: zIndex || 40 }),
    multiValue: (provided) => ({ ...provided, backgroundColor: "#E5E7EB", borderRadius: 8, zIndex: zIndex || 40 }),
    multiValueLabel: (provided) => ({ ...provided, color: "#111827", fontSize: "0.875rem", zIndex: zIndex || 40 }),
    multiValueRemove: (provided) => ({ ...provided, color: "#6B7280", ":hover": { backgroundColor: "#D1D5DB", color: "#111827" }, zIndex: zIndex || 40 }),
    dropdownIndicator: (provided) => ({ ...provided, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "auto" }),
    indicatorSeparator: () => ({ display: "none" }),
  }

  return (
    <div className={clsx(inputTextStyle, "flex flex-col gap-[4px] w-full justify-center", className)}>
      {label && <p className="text-[10px] leading-[14px] uppercase font-normal whitespace-nowrap">{label}</p>}
      <Select
        value={selectedOption}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        isMulti={isMulti}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        styles={customStyles}
        className="text-sm cursor-pointer"
        classNamePrefix="custom-select"
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        components={{
          DropdownIndicator: showDropdownIcon
            ? () => <div>{dropdownIcon ?? <ArrowDropDownIcon />}</div>
            : () => null,
          IndicatorSeparator: () => null,
        }}
        menuPortalTarget={document.body}   // 🚀 render at body level
        menuPosition="fixed"
      />
    </div>
  )
}

